// packages/ui/src/components/ExactValue.tsx
// `React` is in scope on purpose: `tsx --test` compiles this file with the root tsconfig's `jsx: preserve`,
// which it runs as the classic runtime (`React.createElement`). The apps' own builds use the automatic runtime.
import React from 'react';
import type { CSSProperties } from 'react';

import { EXACT_VALUE_ATTR } from './exactValueAttr';

export interface ExactValueProps {
  /** The exact value as it must appear: already formatted by the caller. Exactly one child. */
  children: string | number;
  className?: string;
  style?: CSSProperties;
}

/**
 * A value that must appear EXACTLY as rendered — money, a count, a date or time, a code — inside text a browser
 * translator may rewrite. translate="no" + .notranslate keep engines out; ONE string child keeps React on its
 * self-healing setTextContent path; key={text} makes a change a replacement (every engine sees new content, and
 * the embed guard rebuilds the run around it); data-tt-exact carries the value so the guard can verify it
 * (docs/embed-browser-translation.md).
 *
 * `children: string | number` makes `<ExactValue>{a} {b}</ExactValue>` a type error: build the one string first,
 * and keep labels outside the island. It is a plain inline `<span>` with no styling of its own.
 */
export function ExactValue({ children, className, style }: ExactValueProps): React.JSX.Element {
  const text = String(children);
  return (
    <span
      key={text}
      translate="no"
      className={className ? `notranslate ${className}` : 'notranslate'}
      style={style}
      {...{ [EXACT_VALUE_ATTR]: text }}
    >
      {text}
    </span>
  );
}
